import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Briefcase } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import TopBarActions from '../../components/Header/TopBarActions';
import { useStudentOverview, useStudentCases, useStudentParent, useStudentCycles } from '../api/hooks';
import { SchoolApi } from '../api/client';
import NoticeComposer from '../../pages/SchoolTasks/components/NoticeComposer';
import { remediationCycleNumber } from './student_360/lib/derive';
import HeaderBand from './student_360/HeaderBand';
import OverviewTab from './student_360/OverviewTab';
import AcademicsTab from './student_360/academics/AcademicsTab';
import RemediationCard from './student_360/components/RemediationCard';
import CommitmentsTab from './student_360/CommitmentsTab';
import CasesNotesTab from './student_360/CasesNotesTab';
import OpenCaseDialog from '../components/OpenCaseDialog';
import WellbeingTabs from './student_360/WellbeingTabs';
import ProfileRail from './student_360/ProfileRail';
import { useAuth } from '../../utils/Auth/AuthProvider';
import CoachLaunchButton from './student_360/coach/CoachLaunchButton';
import { useStudentReportExport } from './student_360/export/useStudentReportExport';
import { PanelCard, Spinner, ErrorPanel, Tabs } from '../components/ui';

type Tab = 'overview' | 'academics' | 'study_plan' | 'cases' | 'life';

const TABS: { key: Tab; label: string; help: string }[] = [
  { key: 'overview',   label: 'Overview',      help: 'The student at a glance: risk score, weekly summary, study plan, anomalies, weakest chapters and open alerts.' },
  { key: 'academics',  label: 'Academics',     help: 'Exams question by question, progress, strengths and gaps, practice and tasks, and national exam readiness.' },
  { key: 'study_plan', label: 'Study plan',    help: 'tab_study_plan' },
  { key: 'cases',      label: 'Cases & notes', help: 'tab_cases_notes' },
  { key: 'life',       label: 'Wellbeing & life', help: 'Engagement, wellbeing check-ins, interests and family contact for this student.' },
];
const TAB_KEYS = TABS.map((t) => t.key);
// Links from before the regrouping keep working.
const OLD_TABS: Record<string, Tab> = { performance: 'academics', wellbeing: 'life' };

export default function StudentProfile360() {
  const { studId } = useParams();
  const id = Number(studId);
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get('tab');
  const tab: Tab = TAB_KEYS.includes(rawTab as Tab) ? (rawTab as Tab) : (OLD_TABS[rawTab ?? ''] ?? 'overview');
  const setTab = (t: Tab, view?: string) => {
    const next = new URLSearchParams();
    if (t !== 'overview') next.set('tab', t);
    if (view) next.set('view', view);
    setSp(next, { replace: true });
  };
  const { role } = useAuth();
  const overview = useStudentOverview(id);
  const cases = useStudentCases(id);
  const caseCount = cases.data?.cases.length ?? 0;
  const report = useStudentReportExport(id, overview.data);
  const parent = useStudentParent(id);
  const cycles = useStudentCycles(id);
  const [messageOpen, setMessageOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades() });
  const sectionId = overview.data?.section?.id ?? null;
  const gradeId = sectionId == null ? null
    : (gradesQ.data?.grades ?? []).find((g) => (g.sections ?? []).some((s) => s.id === sectionId))?.id ?? null;

  if (overview.isLoading) {
    return (
      <PanelCard>
        <div className="flex justify-center py-8"><Spinner size="md" /></div>
      </PanelCard>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <PanelCard>
        <ErrorPanel title="Student not found" body="The student you're looking for doesn't exist or isn't accessible." />
      </PanelCard>
    );
  }

  return (
    <div className="space-y-[18px]">
      <TopBarActions>
        <Link to="/students" aria-label="Back to students"
          className="order-first inline-grid h-11 w-11 place-items-center rounded-[12px] border border-line bg-surface text-ink hover:bg-surface-2">
          <ArrowLeft size={18} strokeWidth={1.8} aria-hidden />
        </Link>
        <button type="button" onClick={() => setCaseOpen(true)}
          className="order-last inline-flex min-h-[44px] items-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy hover:brightness-105">
          <Briefcase size={18} strokeWidth={1.8} aria-hidden="true" />Open a case
        </button>
      </TopBarActions>
      <HeaderBand overview={overview.data} onAddNote={() => { setTab('cases'); setNoteOpen(true); }}
        onExportReport={report.exportReport} exporting={report.busy}
        guardian={parent.data ?? null}
        remediationCycle={remediationCycleNumber(cycles.data?.cycles)}
        onMessageStudent={role === 'school_admin' && gradeId != null ? () => setMessageOpen(true) : undefined} />
      {(report.error || report.notice) && (
        <p role={report.error ? 'alert' : 'status'} className={`text-sm ${report.error ? 'text-danger' : 'text-muted'}`}>
          {report.error ?? report.notice}
        </p>
      )}
      <div className="grid gap-[18px] min-[1180px]:grid-cols-[minmax(0,1fr)_316px]">
        <PanelCard className="min-w-0" aria-label="Student detail">
          <Tabs<Tab> inCard label="Profile sections"
            items={TABS.map((t) => {
              if (t.key === 'cases' && caseCount > 0) return { ...t, count: caseCount, countTone: 'danger' as const };
              // The wellbeing tab carries a flag when the colour is "concern" (teachers see the colour only).
              if (t.key === 'life' && overview.data.kpis.wellbeing_dot === 'concern') return { ...t, count: 1, countTone: 'danger' as const };
              return t;
            })}
            value={tab} onChange={setTab} />
          <div>
            {tab === 'overview'   && <OverviewTab id={id} overview={overview.data} onSeePerformance={() => setTab('academics', 'gaps')} onSeePlan={() => setTab('study_plan')} />}
            {tab === 'academics'  && <AcademicsTab id={id} cohort={{ gradeId, sectionId }} studentName={overview.data.profile.first_name || 'Student'} />}
            {tab === 'study_plan' && <CommitmentsTab id={id} cycles={<RemediationCard id={id} />} />}
            {tab === 'cases'      && (
              <CasesNotesTab id={id} studentName={overview.data.profile.first_name} gender={overview.data.profile.gender}
                fullName={`${overview.data.profile.first_name} ${overview.data.profile.last_name}`.trim()}
                noteOpen={noteOpen} onNoteOpenChange={setNoteOpen} />
            )}
            {tab === 'life'       && <WellbeingTabs id={id} cohort={{ gradeId, sectionId }} gender={overview.data.profile.gender} />}
          </div>
        </PanelCard>
        <ProfileRail studentId={id} overview={overview.data} isAdmin={role === 'school_admin'} />
      </div>
      <CoachLaunchButton scope={{ type: 'student', id }} />
      <OpenCaseDialog isOpen={caseOpen} onClose={() => setCaseOpen(false)}
        student={{ id, name: `${overview.data.profile.first_name} ${overview.data.profile.last_name}`.trim() || overview.data.profile.username }} />
      {role === 'school_admin' && gradeId != null && (
        <NoticeComposer isOpen={messageOpen} onClose={() => setMessageOpen(false)} isAdmin
          initial={{ mode: 'students', gradeId, studentIds: [id] }} />
      )}
    </div>
  );
}
